import {
  MODAL_INTEGRATIONS,
  MODAL_NODE_GALLERY_FIGMA_LINK,
} from '@components/App/Modal/constants'
import { Tooltip } from '@lib/components'
import { Plus, Upload } from '@octopus-do/octopus-storybook/icons'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import { uploadImage } from '@storeOld/reducers/tree'
import { openModal } from '@storeOld/reducers/ui'
import cx from 'classnames'
import type React from 'react'
import { useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { ACCEPTED_FILE_TYPES } from '@/constants/constants'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import IconFigma from '../../icons/IconFigma'
import { Loading } from '../Loading'
import css from './Actions.module.scss'

type Props = {
  id: string
  readOnly: boolean
}

const Actions: React.FC<Props> = ({ id, readOnly }) => {
  const ref = useRef<HTMLInputElement>(null)

  const [loading, setLoading] = useState(false)

  const dispatch = useDispatch()

  const isFigmaEnabled = useSelector(
    migrationProjectSelector.getProjectIsFigmaEnabled
  )

  const isOwner = useSelector(
    migrationProjectSelector.getIsCurrentUserOwnerOfProject
  )

  const isProjectTeam = useSelector(migrationProjectSelector.getProjectTeam)

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]

    if (file) {
      onUpload(file)
    }
  }

  const onUpload = async (file: File) => {
    setLoading(true)

    try {
      await dispatch(
        // @ts-expect-error
        uploadImage({
          file,
          nodeId: id,
        })
      )
    } catch (e) {
      console.error('upload image:', e)
    } finally {
      setLoading(false)
    }
  }

  const onClickUpload = () => {
    if (readOnly) {
      return
    }

    sendGtmEvent(GtmTriggerName.IMAGE_UPLOAD, 'Click on upload image icon')

    ref.current?.click()
  }

  const onClickFigma = () => {
    if (readOnly) {
      return
    }

    if (!isFigmaEnabled) {
      if (!isOwner) {
        if (!isProjectTeam) {
          return dispatch(
            notificationActionCreators.setNotification({
              type: NotificationType.FIGMA_ACCOUNT_OWNER,
            })
          )
        }

        if (isProjectTeam) {
          return dispatch(
            notificationActionCreators.setNotification({
              type: NotificationType.FIGMA_ACCOUNT_ADMIN,
            })
          )
        }
      }

      return dispatch(openModal(MODAL_INTEGRATIONS))
    }

    sendGtmEvent(GtmTriggerName.IMAGE_FIGMA, 'Click on Figma')

    dispatch(
      // @ts-expect-error
      openModal(MODAL_NODE_GALLERY_FIGMA_LINK, {
        nodeId: id,
      })
    )
  }

  if (loading) {
    return (
      <div className={css.loading}>
        <div>
          <Loading />
        </div>
      </div>
    )
  }

  return (
    <div className={css.container}>
      <div className={css.plus}>
        <Plus />
      </div>

      <div className={css.actions}>
        <Tooltip content="Upload" position="top">
          <div onClick={onClickUpload} className={css.button}>
            <Upload />
          </div>
        </Tooltip>

        <Tooltip content="Figma" position="top">
          <div
            onClick={onClickFigma}
            className={cx(css.button, {
              [css.disabled]: !isFigmaEnabled && !isOwner,
            })}
          >
            <IconFigma />
          </div>
        </Tooltip>
      </div>

      {!readOnly && (
        <input
          type="file"
          onChange={onChange}
          accept={ACCEPTED_FILE_TYPES.join(',')}
          ref={ref}
        />
      )}
    </div>
  )
}

export default Actions
